import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Notification as AppNotification } from '@vidmakr/bindings/types';
import { parseNotificationPrefs, type NotificationPrefs, type WatchLevel } from '@vidmakr/shared';
import { useSession } from './session';
import { reducers } from './stdb';
import { attempt } from './toast';

export type { AppNotification };

const newestFirst = (a: AppNotification, b: AppNotification) =>
  a.createdAt.microsSinceUnixEpoch > b.createdAt.microsSinceUnixEpoch ? -1 : a.createdAt.microsSinceUnixEpoch < b.createdAt.microsSinceUnixEpoch ? 1 : 0;

/** The signed-in person's in-app notifications, newest first. */
export function useNotifications() {
  const [rows, ready] = useTable(tables.notification);
  return useMemo(() => {
    const list = rows.filter((n) => n.inApp).sort(newestFirst);
    return { list, unread: list.filter((n) => !n.readAt).length, ready };
  }, [rows, ready]);
}

export function useNotificationPrefs(): NotificationPrefs {
  const [rows] = useTable(tables.notificationSettings);
  const userId = useSession((s) => s.user?.userId);
  const json = rows.find((r) => r.userId === userId)?.prefs ?? '';
  return useMemo(() => parseNotificationPrefs(json), [json]);
}

/**
 * Whether the signed-in person follows a project: their own setting, else
 * following it as its creator.
 */
export function useProjectWatch(project: { id: string; createdByUserId: string } | null): { level: WatchLevel | null; following: boolean } {
  const [rows] = useTable(tables.projectWatch);
  const userId = useSession((s) => s.user?.userId);
  const level = (project && rows.find((w) => w.projectId === project.id && w.userId === userId)?.level) as WatchLevel | undefined;
  const following = level ? level === 'all' : !!project && project.createdByUserId === userId;
  return { level: level ?? null, following };
}

/** People in an organization who can be @mentioned (everyone but me). */
export function useMentionCandidates(orgId: string | undefined) {
  const [members] = useTable(tables.orgMember);
  const userId = useSession((s) => s.user?.userId);
  return useMemo(
    () =>
      members
        .filter((m) => m.orgId === orgId && m.userId !== userId)
        .map((m) => ({ userId: m.userId, name: m.name || m.email.split('@')[0], email: m.email, avatarUrl: m.avatarUrl }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members, orgId, userId],
  );
}

export function markRead(ids: string[], read = true) {
  if (!ids.length) return Promise.resolve(true);
  return attempt(() => reducers().markNotificationsRead({ ids, read }));
}

export function markAllRead(projectId = '') {
  return attempt(() => reducers().markAllNotificationsRead({ projectId }));
}

export function removeNotifications(ids: string[]) {
  if (!ids.length) return Promise.resolve(true);
  return attempt(() => reducers().deleteNotifications({ ids }));
}

// ---------------------------------------------------------------------------
// Desktop notifications (per browser, opt-in)
// ---------------------------------------------------------------------------

const DESKTOP_KEY = 'vm:desktopNotifications';

export function desktopSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function desktopEnabled(): boolean {
  try {
    return desktopSupported() && Notification.permission === 'granted' && localStorage.getItem(DESKTOP_KEY) === '1';
  } catch {
    return false;
  }
}

/** Turns desktop notifications on (asking the browser) or off. Resolves to whether they are on. */
export async function setDesktopEnabled(on: boolean): Promise<boolean> {
  if (!desktopSupported()) return false;
  if (on && Notification.permission !== 'granted') {
    const p = await Notification.requestPermission().catch(() => 'denied' as NotificationPermission);
    if (p !== 'granted') return false;
  }
  try {
    localStorage.setItem(DESKTOP_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
  return on;
}
